import { count } from '../core/words.js';
/**
 * What the building is saying, when you are in a state to hear it.
 *
 * There is no line of dialogue in this file. Every sentence is assembled out of
 * something that is true at the moment it is said: how many linens are actually
 * on the shelf, how long until a particular event is actually due, how much a
 * department actually thinks of you, what time somebody actually goes home.
 * Change the state and the sentence changes; there is nothing to run out of and
 * nothing to repeat, because it is a reading rather than a script.
 *
 * Which is also why it is worth hearing. A canned line is flavour. A line that
 * says the Shop Foreman leaves in nine minutes is the reason you are about to
 * run.
 *
 * Ranked by how much it would cost you not to know. The soonest deadline beats
 * the biggest shortfall beats a door about to close beats how anybody feels.
 */

const soon = (minutes) =>
  minutes < 1 ? 'any minute' : minutes < 60 ? count(Math.round(minutes), 'minute') : count(Math.round(minutes / 60), 'hour');

export function createChatter({ read }) {
  /**
   * Everything anyone could currently say, most useful first.
   *
   * `read()` supplies the live numbers. Nothing in here fetches anything, so
   * the same function answers for a test with three made-up facts in it.
   */
  function facts() {
    const state = read() ?? {};
    const out = [];

    for (const event of state.due ?? []) {
      out.push({
        weight: 1000 - event.minutes,
        text: `${event.code} wants ${event.tables} on the floor in ${soon(event.minutes)}.`,
      });
    }

    for (const short of state.shortfalls ?? []) {
      if (short.resolved) continue;
      out.push({
        weight: 500 + (short.amount ?? 1) * 10,
        text: short.stock > 0
          ? `There's ${short.stock} ${short.label.toLowerCase()} left and they want ${short.amount}.`
          : `${short.label}: the shelf is empty, that's ${short.owner ?? 'somebody'}'s problem now.`,
      });
    }

    for (const person of state.leaving ?? []) {
      if (person.minutes > 90) continue;
      out.push({
        weight: 400 - person.minutes,
        text: `${person.name} is off in ${soon(person.minutes)}. After that nobody can sign for it.`,
      });
    }

    for (const owed of state.owed ?? []) {
      out.push({ weight: 300, text: `${owed.name} is still waiting on you for ${owed.what}.` });
    }

    for (const [department, value] of Object.entries(state.standing ?? {})) {
      if (Math.abs(value) < 12) continue;
      out.push({
        weight: 100 + Math.abs(value),
        text: value > 0
          ? `${department} would do you a favour right now, honestly.`
          : `${department} are still sore about it, whatever it was.`,
      });
    }

    return out.sort((a, b) => b.weight - a.weight);
  }

  return {
    facts,
    /**
     * One line, from whoever is nearest. `pick` is an index rather than a roll,
     * so the same building at the same moment says the same thing and the
     * player can walk away and come back to check they heard it right.
     */
    line(pick = 0) {
      const all = facts();
      if (!all.length) return null;
      return all[Math.min(pick, all.length - 1)].text;
    },
  };
}
